import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  plan: string;
  subscriptionLoading: boolean;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  plan: "free",
  subscriptionLoading: true,
  signOut: async () => {},
  refreshSubscription: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("free");
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkInFlightRef = useRef(false);
  const lastCheckRef = useRef(0);

  const checkSubscription = useCallback(async (currentSession: Session | null) => {
    if (!currentSession?.access_token) {
      setPlan("free");
      setSubscriptionLoading(false);
      return;
    }

    // Dedup: skip if already in-flight or checked within last 5s
    const now = Date.now();
    if (checkInFlightRef.current || now - lastCheckRef.current < 5000) {
      return;
    }
    checkInFlightRef.current = true;

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });

      if (error) {
        console.error("[AuthContext] check-subscription error:", error);
        return;
      }

      if (data?.plan) {
        setPlan(data.plan);
      }
    } catch (err) {
      console.error("[AuthContext] check-subscription failed:", err);
    } finally {
      checkInFlightRef.current = false;
      lastCheckRef.current = Date.now();
      setSubscriptionLoading(false);
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    // Force bypass dedup for manual refresh
    lastCheckRef.current = 0;
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    await checkSubscription(currentSession);
  }, [checkSubscription]);

  useEffect(() => {
    let initialCheckDone = false;

    // 1. Set up listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session) {
          // Only check subscription from listener if initial check already ran
          if (initialCheckDone) {
            checkSubscription(session);
          }
        } else {
          setPlan("free");
          setSubscriptionLoading(false);
        }
      }
    );

    // 2. Then get initial session — single check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      initialCheckDone = true;
      checkSubscription(session);
    });

    // 3. Auto-refresh every 5 minutes
    intervalRef.current = setInterval(() => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          checkSubscription(session);
        }
      });
    }, 300000);

    return () => {
      subscription.unsubscribe();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setPlan("free");
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, plan, subscriptionLoading, signOut, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};
